import React, { ReactNode, useEffect } from 'react';

import { useActiveState, useScreenSize } from '@hcs/hooks';

import { UserProfileIcon } from '../../../../svgs/icons/generics';
import { SlideInPane } from '../../drawers';

import styles from './UserMenu.module.css';

const dataHcName = 'global-user-menu';

export interface UserMenuProps {
  content: ReactNode;
  onToggle?: (visible: boolean) => void;
}

export const UserMenu = ({ content, onToggle }: UserMenuProps) => {
  const { isPhoneWidth } = useScreenSize();
  const { active, handleToggle, handleClose } = useActiveState();
  const lightTrigger = (
    <UserProfileIcon
      className={styles.Icon}
      dataHcName={`${dataHcName}-icon`}
      color="neutral-light-10"
    />
  );
  const darkTrigger = (
    <UserProfileIcon
      className={styles.Icon}
      dataHcName={`${dataHcName}-icon`}
      color="neutral-dark-20"
    />
  );

  // Close the menu when most click events inside the menu bubble up to the menu container (meaning user
  // has clicked within the menu)
  //
  // Note that in most cases the menu will close due to the unmounting of this component due to the
  // navigation to a new route. This handling is only needed to cover edge cases where menu item
  // button clicks don't result in route navigation (e.g. instead open a modal on the current page)
  const handleContainerClick = (e: React.MouseEvent) => {
    if (
      active &&
      e.target &&
      // if the click is within an SVG element, close the menu
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (typeof (e.target as HTMLElement).className !== 'string' ||
        // if the click is NOT in the background of the pane, close the menu
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !(e.target as HTMLElement).className.includes(
          styles.ScrollableContainer,
        ))
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    if (onToggle) {
      onToggle(active);
    }
  }, [active, onToggle]);

  return (
    <div onClick={handleContainerClick}>
      <button
        className={styles.TriggerButton}
        name="User Menu"
        onClick={handleToggle}
      >
        {isPhoneWidth ? lightTrigger : darkTrigger}
      </button>
      <SlideInPane
        active={active}
        onClose={handleClose}
        expandedWidth={isPhoneWidth ? '293px' : '400px'}
        dataHcName={dataHcName}
        className={styles.SlideInPane}
        transparentScreen
        fromSide="right"
        theme={{
          Pane: styles.Pane,
          ScrollableContainer: styles.ScrollableContainer,
        }}
      >
        {content}
      </SlideInPane>
    </div>
  );
};
